import { Box, Chip, Dropdown, Menu, MenuButton } from '@mui/joy';
import { Column, GridApi, ICellRendererParams } from 'ag-grid-community';
import { MouseEvent, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { TextEllipsis } from 'core/elements';
import { Color, Size, Variant } from 'theme/enums';

// How a chip matches each text filter condition; the negative ones never point at a chip.
const CHIP_MATCHERS: Record<string, (chip: string, text: string) => boolean> = {
  contains: (chip, text) => chip.includes(text),
  equals: (chip, text) => chip === text,
  startsWith: (chip, text) => chip.startsWith(text),
  endsWith: (chip, text) => chip.endsWith(text),
};

interface IFilterTerm {
  text: string;
  type: string;
}

interface ITextFilterModel {
  conditions?: ITextFilterModel[];
  filter?: string;
  filterType?: string;
  type?: string;
}

const getFilterTerms = (model: ITextFilterModel | null): IFilterTerm[] => {
  if (!model) {
    return [];
  }

  if (model.conditions) {
    return model.conditions.flatMap(getFilterTerms);
  }

  if ('text' !== model.filterType || !CHIP_MATCHERS[model.type] || !model.filter) {
    return [];
  }

  return [{ text: model.filter.toLowerCase(), type: model.type }];
};

const matchesFilterTerms = (chip: string, filterTerms: IFilterTerm[]) =>
  filterTerms.some(({ text, type }) => CHIP_MATCHERS[type](chip.toLowerCase(), text));

// The column's active text filter terms, kept current as the filter changes.
const useColumnFilterTerms = (api: GridApi, column: Column, isEnabled: boolean) => {
  const [filterTerms, setFilterTerms] = useState<IFilterTerm[]>([]);

  useEffect(() => {
    if (!isEnabled || !api || !column) {
      return;
    }

    const updateFilterTerms = () =>
      setFilterTerms(getFilterTerms(api.getColumnFilterModel<ITextFilterModel>(column)));

    updateFilterTerms();
    api.addEventListener('filterChanged', updateFilterTerms);

    return () => {
      if (!api.isDestroyed()) {
        api.removeEventListener('filterChanged', updateFilterTerms);
      }
    };
  }, [api, column, isEnabled]);

  return filterTerms;
};

interface IProps extends ICellRendererParams {
  getChipValue?: (value: string) => string;
  getChipStartDecorator?: (value: string) => ReactNode;
  // Tints the chips that match the column's text filter, so the value a row was found by stands
  // out.
  highlightFilterMatches?: boolean;
  // Keeps the chips on one line: as many as fit the column, then a "+ N more" button that opens
  // all of them (only the button when not even one fits).
  fitToWidth?: boolean;
  placeholder?: string;
}

// A click on the button or in its menu must not reach the grid, which would select the row.
const stopPropagation = (event: MouseEvent) => event.stopPropagation();

const CHIP_GAP = 8;

const CHIP_LIST_SX = { display: 'flex', flexWrap: 'wrap', padding: '4px 0', gap: `${CHIP_GAP}px` };

const CHIP_LINE_SX = {
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  gap: `${CHIP_GAP}px`,
  height: '100%',
  overflow: 'hidden',
};

// Laid out like the line but invisible, so every chip and the button can be measured.
const CHIP_MEASURE_SX = {
  ...CHIP_LINE_SX,
  position: 'absolute',
  visibility: 'hidden',
  pointerEvents: 'none',
  height: 0,
};

// How many chips fit the width: all of them when they fit without the button, otherwise as many
// as leave room for it.
export const countFittingChips = (
  chipWidths: number[],
  availableWidth: number,
  moreWidth: number
) => {
  const allChipsWidth = chipWidths.reduce((total, width) => total + width, 0);

  if (allChipsWidth + CHIP_GAP * (chipWidths.length - 1) <= availableWidth) {
    return chipWidths.length;
  }

  let usedWidth = moreWidth;
  let count = 0;

  while (count < chipWidths.length && usedWidth + CHIP_GAP + chipWidths[count] <= availableWidth) {
    usedWidth += CHIP_GAP + chipWidths[count];
    count += 1;
  }

  return count;
};

interface IFittedChipLineProps {
  chipValues: string[];
  renderChip: (chipValue: string, index: number) => JSX.Element;
  isMatch: (chipValue: string) => boolean;
}

const FittedChipLine = ({ chipValues, renderChip, isMatch }: IFittedChipLineProps) => {
  const lineRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  const [fittingCount, setFittingCount] = useState(chipValues.length);

  const { t } = useTranslation();

  useLayoutEffect(() => {
    const line = lineRef.current;
    const measure = measureRef.current;

    if (!line || !measure) {
      return;
    }

    const updateFittingCount = () => {
      const widths = Array.from(measure.children).map(
        (child) => (child as HTMLElement).offsetWidth
      );
      const moreWidth = widths.pop() ?? 0;

      setFittingCount(countFittingChips(widths, line.clientWidth, moreWidth));
    };

    updateFittingCount();

    const resizeObserver = new ResizeObserver(updateFittingCount);

    resizeObserver.observe(line);

    return () => resizeObserver.disconnect();
  }, [chipValues]);

  const hiddenChipValues = chipValues.slice(fittingCount);

  const moreButton = (
    <MenuButton
      size={Size.SM}
      variant={Variant.SOFT}
      color={hiddenChipValues.some(isMatch) ? Color.PRIMARY : Color.NEUTRAL}
      onClick={stopPropagation}
      sx={{ borderRadius: 'xl', flexShrink: 0 }}
    >
      {t('labelMoreCount', { count: hiddenChipValues.length || chipValues.length })}
    </MenuButton>
  );

  return (
    <Box sx={{ position: 'relative', height: '100%' }}>
      <Box ref={measureRef} sx={CHIP_MEASURE_SX} aria-hidden>
        {chipValues.map(renderChip)}
        <Dropdown>{moreButton}</Dropdown>
      </Box>

      <Box ref={lineRef} sx={CHIP_LINE_SX}>
        {chipValues.slice(0, fittingCount).map(renderChip)}
        {hiddenChipValues.length > 0 && (
          <Dropdown>
            {moreButton}
            <Menu onClick={stopPropagation} sx={{ maxWidth: 480, padding: 1.5 }}>
              <Box sx={CHIP_LIST_SX}>{chipValues.map(renderChip)}</Box>
            </Menu>
          </Dropdown>
        )}
      </Box>
    </Box>
  );
};

export const ChipListCell = ({
  value: chipValues = [],
  api,
  column,
  getChipValue,
  getChipStartDecorator,
  highlightFilterMatches = false,
  fitToWidth = false,
  placeholder = DEFAULT_PLACEHOLDER,
}: IProps) => {
  const filterTerms = useColumnFilterTerms(api, column, highlightFilterMatches);

  if (!chipValues || chipValues.length === 0) {
    return <>{placeholder}</>;
  }

  const getValue = (chipValue: string) => (getChipValue ? getChipValue(chipValue) : chipValue);
  const isMatch = (chipValue: string) => matchesFilterTerms(`${getValue(chipValue)}`, filterTerms);

  const renderChip = (chipValue: string, index: number) => (
    <Chip
      key={`${getValue(chipValue)}-${index}`}
      variant={isMatch(chipValue) ? Variant.SOFT : Variant.OUTLINED}
      color={isMatch(chipValue) ? Color.PRIMARY : undefined}
      startDecorator={getChipStartDecorator?.(chipValue)}
    >
      {getValue(chipValue)}
    </Chip>
  );

  if (fitToWidth) {
    return <FittedChipLine chipValues={chipValues} renderChip={renderChip} isMatch={isMatch} />;
  }

  return (
    <TextEllipsis
      content={<Box sx={CHIP_LIST_SX}>{chipValues.map(renderChip)}</Box>}
      lineHeight={32}
      maxRows={3}
      showMoreLabel
    />
  );
};
